import { useEffect, useState } from "react";
import { api, errMessage } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { useAuth } from "@/context/AuthContext";
import { Gavel, Users, BarChart3, AlertCircle, CheckCircle2, FileCheck } from "lucide-react";
import { toast } from "sonner";

const RECOMMENDATIONS = [
  { value: "approval",             label: "Recommend Approval",             tone: "bg-emerald-100 text-emerald-800 border-emerald-200" },
  { value: "conditional_approval", label: "Recommend Conditional Approval", tone: "bg-blue-100 text-blue-800 border-blue-200" },
  { value: "probation",            label: "Recommend Probation",            tone: "bg-amber-100 text-amber-800 border-amber-200" },
  { value: "denial",               label: "Recommend Denial",               tone: "bg-red-100 text-red-700 border-red-200" },
];

const STANDARDS_LABEL = {
  institutional_governance:   "Institutional Governance",
  faculty_qualifications:     "Faculty Qualifications",
  curriculum_quality:         "Curriculum Quality",
  learning_outcomes:          "Learning Outcomes",
  student_records_management: "Student Records Management",
  assessment_and_evaluation:  "Assessment and Evaluation",
  ethics_and_compliance:      "Ethics and Compliance",
  operational_sustainability: "Operational Sustainability",
};

export default function DecisionPanel({ applicationId, accreditationType = "institutional" }) {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [scoresData, setScoresData] = useState(null);
  const [recs, setRecs] = useState([]);
  const [findings, setFindings] = useState([]);
  const [recommendation, setRecommendation] = useState("");
  const [summary, setSummary] = useState("");
  const [busy, setBusy] = useState(false);

  const reload = async () => {
    try {
      const [s, r, f] = await Promise.all([
        api.get("/reviewer-scores", { params: { school_application_id: applicationId } }),
        api.get("/reviewer-recommendations", { params: { school_application_id: applicationId } }),
        api.get("/compliance-findings", { params: { school_application_id: applicationId } }),
      ]);
      setScoresData(s.data);
      setRecs(r.data);
      setFindings(f.data);
    } catch (e) {
      // Non-fatal — empty state
    }
  };
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [applicationId]);

  const submit = async () => {
    if (!recommendation) {
      toast.error("Pick a recommendation first");
      return;
    }
    if (!summary || summary.trim().length < 20) {
      toast.error("Add a reviewer summary (min 20 characters) before recommending");
      return;
    }
    setBusy(true);
    try {
      await api.post("/reviewer-recommendations", {
        school_application_id: applicationId,
        recommendation,
        summary,
        scoring_phase: "committee",
      });
      toast.success("Recommendation submitted");
      setSummary("");
      reload();
    } catch (e) {
      toast.error(errMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const outstanding = findings.filter((f) => !["corrected", "verified", "closed"].includes(f.resolution_status));

  return (
    <div className="space-y-5" data-testid="decision-panel">
      {/* Snapshot stats */}
      <div className="grid sm:grid-cols-3 gap-3">
        <Card className="p-4 border-slate-200">
          <div className="flex items-center gap-2 text-xs uppercase tracking-[0.18em] font-bold text-slate-500 mb-1">
            <BarChart3 className="h-3.5 w-3.5" /> Overall score
          </div>
          <div className="text-2xl font-heading font-bold text-primary" data-testid="dp-overall">
            {scoresData?.overall_score != null ? `${scoresData.overall_score}%` : "—"}
          </div>
        </Card>
        <Card className="p-4 border-slate-200">
          <div className="flex items-center gap-2 text-xs uppercase tracking-[0.18em] font-bold text-slate-500 mb-1">
            <Users className="h-3.5 w-3.5" /> Reviewer count
          </div>
          <div className="text-2xl font-heading font-bold text-primary" data-testid="dp-reviewers">
            {scoresData?.reviewer_count || 0}
          </div>
        </Card>
        <Card className="p-4 border-slate-200">
          <div className="flex items-center gap-2 text-xs uppercase tracking-[0.18em] font-bold text-slate-500 mb-1">
            <AlertCircle className="h-3.5 w-3.5" /> Outstanding findings
          </div>
          <div className="text-2xl font-heading font-bold text-primary" data-testid="dp-outstanding">
            {outstanding.length}
          </div>
          {outstanding.length > 0 && (
            <div className="text-[10px] text-slate-500 mt-0.5">
              of {findings.length} total
            </div>
          )}
        </Card>
      </div>

      {/* Category breakdown */}
      {scoresData?.by_category_avg && Object.values(scoresData.by_category_avg).some((v) => v != null) && (
        <Card className="p-5 border-slate-200">
          <h3 className="font-heading text-sm font-bold text-primary mb-3">Category breakdown</h3>
          <div className="space-y-2">
            {Object.entries(scoresData.by_category_avg).map(([id, avg]) => (
              <div key={id} className="flex items-center gap-3" data-testid={`dp-cat-${id}`}>
                <span className="text-xs w-56 text-slate-700">{STANDARDS_LABEL[id] || id}</span>
                <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                  <div
                    className={`h-full ${avg == null ? "" : avg >= 80 ? "bg-emerald-500" : avg >= 50 ? "bg-amber-500" : "bg-red-500"}`}
                    style={{ width: `${avg ?? 0}%` }}
                  />
                </div>
                <span className="text-xs font-mono-data text-slate-600 w-12 text-right">
                  {avg == null ? "—" : `${avg}%`}
                </span>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Existing recommendations */}
      {recs.length > 0 && (
        <Card className="p-5 border-slate-200">
          <h3 className="font-heading text-sm font-bold text-primary mb-3">Current recommendations</h3>
          <div className="space-y-2">
            {recs.map((r) => (
              <div key={r.id} className="flex flex-wrap items-center gap-2 p-2 border-b border-slate-100" data-testid={`dp-rec-${r.id}`}>
                <Badge className={`border capitalize ${
                  RECOMMENDATIONS.find((x) => x.value === r.recommendation)?.tone
                }`}>
                  {RECOMMENDATIONS.find((x) => x.value === r.recommendation)?.label || r.recommendation}
                </Badge>
                <span className="text-[11px] font-mono-data text-slate-500">v{r.version}</span>
                <span className="text-xs text-slate-600">· {r.reviewer_email}</span>
                <Badge variant="outline" className="text-[10px] capitalize">{r.scoring_phase}</Badge>
                {r.summary && <p className="basis-full text-xs text-slate-600 italic">"{r.summary}"</p>}
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Submit new recommendation */}
      <Card className="p-5 border-slate-200">
        <h3 className="font-heading text-sm font-bold text-primary mb-3 flex items-center gap-2">
          <Gavel className="h-4 w-4" /> Submit a recommendation
        </h3>
        <div className="grid sm:grid-cols-2 gap-2 mb-3">
          {RECOMMENDATIONS.map((opt) => (
            <button
              key={opt.value}
              type="button"
              onClick={() => setRecommendation(opt.value)}
              data-testid={`rec-${opt.value}`}
              className={`text-left px-3 py-3 rounded-md border transition ${
                recommendation === opt.value
                  ? opt.tone + " ring-2 ring-primary/30 border-primary/40"
                  : "border-slate-200 hover:border-slate-300 text-slate-700 bg-white"
              }`}
            >
              <span className="text-sm font-semibold">{opt.label}</span>
            </button>
          ))}
        </div>
        <Textarea
          rows={4}
          placeholder="Reviewer comments (required, min 20 chars). Justify your recommendation with reference to specific standards, site visit findings, and any outstanding compliance items."
          data-testid="rec-summary"
          value={summary}
          onChange={(e) => setSummary(e.target.value)}
          className="text-sm mb-3"
        />
        <div className="flex items-center justify-between">
          <span className={`text-[11px] ${summary.length < 20 ? "text-amber-600" : "text-emerald-600"}`}>
            {summary.length}/20+ characters
          </span>
          <Button
            onClick={submit}
            disabled={busy || !recommendation || summary.length < 20}
            className="bg-primary text-white hover:bg-[#113A64]"
            data-testid="rec-submit"
          >
            <CheckCircle2 className="h-4 w-4 mr-1" />
            {busy ? "Submitting…" : "Submit recommendation"}
          </Button>
        </div>
      </Card>

      {isAdmin && (
        <DecisionRecordForm applicationId={applicationId} accreditationType={accreditationType} />
      )}
    </div>
  );
}

const DECISION_STATUS = [
  { value: "approved",                label: "Approved" },
  { value: "conditionally_approved",  label: "Conditionally Approved" },
  { value: "probation",               label: "Probation" },
  { value: "denied",                  label: "Denied" },
  { value: "suspended",               label: "Suspended" },
  { value: "revoked",                 label: "Revoked" },
];

function DecisionRecordForm({ applicationId, accreditationType }) {
  const today = new Date().toISOString().slice(0, 10);
  const fiveYears = new Date(); fiveYears.setFullYear(fiveYears.getFullYear() + 5);
  const [form, setForm] = useState({
    decision_status: "approved",
    reviewer_recommendation: "approval",
    decision_date: today,
    effective_date: today,
    expiration_date: fiveYears.toISOString().slice(0, 10),
    conditions_required: "",
    follow_up_required: false,
    notes: "",
    compliance_score: "",
  });
  const [history, setHistory] = useState([]);
  const [busy, setBusy] = useState(false);

  const reload = async () => {
    try {
      const r = await api.get("/decision-support", { params: { school_application_id: applicationId } });
      setHistory(r.data || []);
    } catch { /* ignore */ }
  };
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [applicationId]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (form.conditions_required && form.conditions_required.length < 5 && form.decision_status === "conditionally_approved") {
      toast.error("Conditions are required for a conditional approval");
      return;
    }
    setBusy(true);
    try {
      const payload = {
        ...form,
        school_application_id: applicationId,
        accreditation_type: accreditationType,
        compliance_score: form.compliance_score === "" ? null : Number(form.compliance_score),
      };
      await api.post("/decision-support", payload);
      toast.success("Accreditation decision recorded");
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not record decision");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="p-5 border-primary/30 bg-primary/[0.02]" data-testid="decision-record-form">
      <h3 className="font-heading text-sm font-bold text-primary mb-3 flex items-center gap-2">
        <FileCheck className="h-4 w-4" /> Record final accreditation decision (admin)
      </h3>

      <div className="grid sm:grid-cols-2 gap-3 mb-3">
        <div>
          <Label className="text-xs">Decision status</Label>
          <Select value={form.decision_status} onValueChange={(v) => set("decision_status", v)}>
            <SelectTrigger data-testid="ds-status"><SelectValue /></SelectTrigger>
            <SelectContent>
              {DECISION_STATUS.map((d) => <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs">Recommendation that informed this decision</Label>
          <Select value={form.reviewer_recommendation} onValueChange={(v) => set("reviewer_recommendation", v)}>
            <SelectTrigger data-testid="ds-recommendation"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="approval">Approval</SelectItem>
              <SelectItem value="conditional_approval">Conditional Approval</SelectItem>
              <SelectItem value="probation">Probation</SelectItem>
              <SelectItem value="denial">Denial</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs">Decision date</Label>
          <Input type="date" data-testid="ds-decision-date" value={form.decision_date} onChange={(e) => set("decision_date", e.target.value)} />
        </div>
        <div>
          <Label className="text-xs">Compliance score (0-100, optional)</Label>
          <Input type="number" min="0" max="100" data-testid="ds-compliance" value={form.compliance_score} onChange={(e) => set("compliance_score", e.target.value)} />
        </div>
        <div>
          <Label className="text-xs">Effective date</Label>
          <Input type="date" data-testid="ds-effective" value={form.effective_date} onChange={(e) => set("effective_date", e.target.value)} />
        </div>
        <div>
          <Label className="text-xs">Expiration date</Label>
          <Input type="date" data-testid="ds-expiration" value={form.expiration_date} onChange={(e) => set("expiration_date", e.target.value)} />
        </div>
      </div>

      <div className="mb-3">
        <Label className="text-xs">Conditions required</Label>
        <Textarea
          rows={2}
          data-testid="ds-conditions"
          value={form.conditions_required}
          onChange={(e) => set("conditions_required", e.target.value)}
          placeholder="If conditional approval or probation, enumerate the conditions that must be met."
        />
      </div>

      <div className="mb-3">
        <Label className="text-xs">Decision summary / reviewer notes</Label>
        <Textarea
          rows={3}
          data-testid="ds-notes"
          value={form.notes}
          onChange={(e) => set("notes", e.target.value)}
          placeholder="Concise rationale referencing the standards, site visit findings, and committee deliberations."
        />
      </div>

      <label className="flex items-center gap-2 cursor-pointer mb-3">
        <Checkbox checked={form.follow_up_required} onCheckedChange={(v) => set("follow_up_required", !!v)} data-testid="ds-followup" />
        <span className="text-xs text-slate-700">Follow-up review required</span>
      </label>

      <div className="flex items-center justify-end">
        <Button onClick={submit} disabled={busy} className="bg-primary text-white hover:bg-[#113A64]" data-testid="ds-submit">
          <Gavel className="h-4 w-4 mr-1" />
          {busy ? "Recording…" : "Record decision"}
        </Button>
      </div>

      {history.length > 0 && (
        <div className="mt-5 pt-4 border-t border-slate-200">
          <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-500 mb-2">Decision history ({history.length})</div>
          <div className="space-y-2">
            {history.map((d) => (
              <div key={d.id} className="flex flex-wrap items-center gap-2 p-2 bg-white border border-slate-200 rounded text-xs" data-testid={`ds-row-${d.id}`}>
                <Badge variant="outline" className="capitalize text-[10px]">{d.decision_status.replace(/_/g, " ")}</Badge>
                <span className="text-slate-600">eff {d.effective_date || "—"} → {d.expiration_date || "—"}</span>
                <span className="text-slate-500">· {d.decided_by}</span>
                <span className="text-slate-400 ml-auto">{new Date(d.created_at).toLocaleDateString()}</span>
                {d.notes && <p className="basis-full text-slate-600 italic mt-1">"{d.notes}"</p>}
              </div>
            ))}
          </div>
        </div>
      )}
    </Card>
  );
}
